/**
 * RouteGuard Component
 *
 * Protects nested routes within dashboards by checking if the current route
 * is allowed for the user's role. Redirects unauthorized users to their
 * appropriate dashboard.
 */

import React from 'react'
import { Navigate, useLocation } from 'react-router-dom'
import useAppState from '../hooks/useAppState'
import { isRouteAllowed, getRouteMetadata } from '../config/routes/routeConfig'
import { LoadingPage } from './LoadingPage'
import { NotAuthorisedPage } from './NotAuthorisedPage'
import { redirectUnauthorizedUser } from '../utils/routeProtection'
import { isPlatformSuperAdmin } from '../../../pure-js/libs/isSuperUser'
import { getActiveOrgRole } from '../features/organization/orgStateHelpers'

interface RouteGuardProps {
  children: React.ReactNode
}

export const RouteGuard: React.FC<RouteGuardProps> = ({ children }) => {
  const { state } = useAppState()
  const location = useLocation()

  // Show loading while auth initializes
  if (!state.isAuthInitialized || !state.user?.id) {
    return <LoadingPage />
  }

  const user = state.user
  const activeOrgRole = getActiveOrgRole(user)
  const currentPath = location.pathname

  if (!activeOrgRole) {
    return <NotAuthorisedPage />
  }

  // Checked before the role list: an org ADMIN satisfies `allowedRoles`, and these routes are for
  // platform operators only.
  if (getRouteMetadata(currentPath)?.adminOnly && !isPlatformSuperAdmin(user)) {
    return <NotAuthorisedPage />
  }

  if (!isRouteAllowed(currentPath, activeOrgRole)) {
    return <Navigate to={redirectUnauthorizedUser(activeOrgRole, currentPath)} replace />
  }

  return <>{children}</>
}
